import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
} from '@mui/icons-material'
import {
  AppBar,
  Box,
  Button,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useAuth } from '~/api/auth'
import { actions as pages } from '~/routes'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import ColorModeContext from '~/theme/ColorModeContext'

const Logo = () => {
  return (
    <span>
      <strong>B/S/H/</strong>
      <sup
        style={{ fontSize: '0.55em', fontWeight: '600', marginLeft: '0.2em' }}
      >
        GEN
      </sup>
    </span>
  )
}

const BrandSelector = () => {
  const { brand } = useRouteContext(BrandSelector.name)
  const brands = useSelector(bshSlice.brands)
  return (
    <FormControl
      size="small"
      sx={{
        display: 'grid',
        minWidth: '6.875rem',
        '& .MuiSelect-select': {
          paddingTop: '0',
          paddingBottom: '0',
        },
        '& .MuiSvgIcon-root': {
          color: 'primary.main',
        },
      }}
    >
      <Select
        sx={{
          backgroundColor: '#fff',
          color: '#000',
        }}
        value={brand.url}
      >
        {brands.map(({ url, name }, index) => (
          <MenuItem
            key={index}
            value={url}
            disabled={brand.url === url}
            component={Link}
            to={url}
          >
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const Navigation = () => {
  const { isAuth, logout } = useAuth()
  const { brand, action, ...routeContext } = useRouteContext(Navigation.name)

  const ready = useSelector(bshSlice.ready)
  const { t } = useTranslation()
  const theme = useTheme()
  const colorMode = useContext(ColorModeContext)
  const [anchorElNav, setAnchorElNav] = useState(null)

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
  }

  const createLink = ({ path, deep = true }) => {
    return [brand.url, path]
      .concat(deep ? [routeContext.model, routeContext.id] : [])
      .filter(Boolean)
      .join('/')
  }

  return (
    <>
      <AppBar position="static" enableColorOnDark={!!brand} sx={{ zIndex: 2 }}>
        <Toolbar
          disableGutters
          variant="dense"
          sx={{ paddingLeft: 2, paddingRight: 2 }}
        >
          <Link
            to={brand?.url ?? '/'}
            className="text-inherit hidden lg:block mr-2 no-underline"
          >
            <Typography variant="h6" noWrap component="span">
              <Logo />
            </Typography>
          </Link>
          {/* mobile nav */}
          {isAuth && brand && ready && (
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', lg: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', lg: 'none' },
                }}
              >
                {pages.map((page, index) => {
                  const current = page.path === action?.path
                  return (
                    <MenuItem
                      key={index}
                      component={Link}
                      to={createLink(page)}
                      disabled={current}
                      onClick={handleCloseNavMenu}
                    >
                      <Typography textAlign="center">{page.name}</Typography>
                    </MenuItem>
                  )
                })}
              </Menu>
            </Box>
          )}
          <Typography
            variant="h5"
            noWrap
            component={Link}
            to={brand?.url ?? '/'}
            sx={{
              mr: 2,
              display: { xs: 'flex', lg: 'none' },
              flexGrow: 1,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Logo />
          </Typography>
          {/* desktop menu */}
          {isAuth && ready && brand ? (
            <Box
              gap={0.5}
              sx={{ flexGrow: 1, display: { xs: 'none', lg: 'flex' } }}
            >
              <BrandSelector />
              {pages.map((page, index) => {
                const current = page.path === action?.path
                let sx = {
                  // backgroundColor: '#3c52b2',
                  border: '1px solid rgba(255, 255, 255, .3)',
                  color: '#fff',
                  padding: '0.1rem 0.5rem',
                  textTransform: 'initial',
                  '&:hover': {
                    backgroundColor: '#fff',
                    color: 'primary.main',
                  },
                }
                if (current) {
                  sx = {
                    ...sx,
                    backgroundColor: '#fff',
                    color: 'primary.main',
                  }
                } else {
                  sx = {
                    ...sx,
                    color: '#fff',
                  }
                }
                return (
                  <Button
                    key={index}
                    component={Link}
                    to={createLink(page)}
                    sx={sx}
                  >
                    {page.name}
                  </Button>
                )
              })}
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}

          <Box>
            <Tooltip title={t('theme.toggle')}>
              <IconButton
                sx={{ ml: 1 }}
                onClick={colorMode.toggle}
                color="inherit"
              >
                {theme.palette.mode === 'dark' ? (
                  <Brightness7Icon />
                ) : (
                  <Brightness4Icon />
                )}
              </IconButton>
            </Tooltip>
            {isAuth && (
              <Tooltip title={t('auth.logout')}>
                <IconButton sx={{ color: 'inherit' }} onClick={() => logout()}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </>
  )
}

export default Navigation
